import React, { useState } from 'react';
import { Modal, Form, Select, Input, Button, Space, Alert, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { CustomerOrder, OrderStatus } from '../types';
import { getSelectableCustomerOrderStatusIds } from '../utils/customerOrderStatus.ts';

const { Option } = Select;
const { TextArea } = Input;

interface OrderStatusModalProps {
  order: CustomerOrder | null;
  orderStatuses: OrderStatus[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  /** true mentre la richiesta è in corso: disabilita "Aggiorna Stato" (anti doppio clic) */
  loading?: boolean;
}

const OrderStatusModal: React.FC<OrderStatusModalProps> = ({
  order,
  orderStatuses,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [selectedStatusId, setSelectedStatusId] = useState<number | undefined>(order?.order_status_id);

  // Opzioni: stato attuale (per primo) + transizioni ammesse, per ID, dalla mappa
  // condivisa con il backend (prima una mappa per NOME ammetteva ritorni indietro
  // come Pronto per Spedizione → In Produzione che il server oggi rifiuta)
  const allAvailableStatuses = React.useMemo(() => {
    if (!order) return [];
    const ids = getSelectableCustomerOrderStatusIds(order.order_status_id);
    return ids
      .map((id) => orderStatuses.find((s) => s.id === id))
      .filter((s): s is OrderStatus => !!s);
  }, [order, orderStatuses]);
  if (!order) return null;

  const handleSubmit = (values: any) => {
    // ✅ Pulisci i dati prima di inviarli - non includere campi null/undefined
    const cleanData: any = {
      order_status_id: values.order_status_id,
    };
    
    // Aggiungi note solo se presenti
    if (values.notes && values.notes.trim()) {
      cleanData.notes = values.notes.trim();
    }
    
    // Aggiungi actual_delivery_date solo se presente e valido
    if (values.actual_delivery_date) {
      try {
        // Assicurati che sia un oggetto dayjs e convertilo in stringa
        if (values.actual_delivery_date.format) {
          cleanData.actual_delivery_date = values.actual_delivery_date.format('YYYY-MM-DD');
        } else if (typeof values.actual_delivery_date === 'string') {
          // Se è già una stringa, convertila in dayjs e poi formattala
          cleanData.actual_delivery_date = dayjs(values.actual_delivery_date).format('YYYY-MM-DD');
        }
      } catch (error) {
        console.error('Errore nella formattazione della data:', error);
        // Fallback: usa la data odierna se c'è un errore
        cleanData.actual_delivery_date = dayjs().format('YYYY-MM-DD');
      }
    }
  
    onSubmit(cleanData); // Invia solo dati puliti
  };
  const getStatusDescription = (statusId: number) => {
    const status = orderStatuses.find(s => s.id === statusId);
    if (!status) return '';

    const descriptions: { [key: string]: string } = {
      'Nuovo': 'L\'ordine è stato creato e attende elaborazione.',
      'In Attesa': 'L\'ordine è in attesa (es. di conferma del cliente o di disponibilità).',
      'In Lavorazione': 'L\'ordine è in fase di elaborazione e verifica.',
      'Confermato': 'L\'ordine è stato confermato e approvato.',
      'In Produzione': 'I prodotti dell\'ordine sono in fase di produzione.',
      'Pronto per Spedizione': 'L\'ordine è completo e pronto per essere spedito.',
      'Spedito': 'L\'ordine è stato spedito al cliente.',
      'In Transito': 'La merce è in viaggio verso il cliente.',
      'Consegnato': 'L\'ordine è stato consegnato al cliente.',
      'Evaso': 'Ordine chiuso: merce scaricata dal magazzino (ordine normale) o entrata (ordine stock). Righe non più modificabili.',
      'Annullato': 'L\'ordine è annullato (stato definitivo): le sue prenotazioni vengono liberate.'
    };

    return descriptions[status.status_name] || '';
  };
  const requiresDeliveryDate = (statusId: number) => {
    const status = orderStatuses.find(s => s.id === statusId);
    return status?.status_name === 'Consegnato';
  };

  return (
    <Modal
      title={`Cambia Stato Ordine - ${order.order_number}`}
      open={true}
      onCancel={onCancel}
      footer={null}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          order_status_id: order.order_status_id,
          // Inizializza la data di consegna solo se lo stato corrente è già "Consegnato"
          actual_delivery_date: order.order_status?.status_name === 'Consegnato' ? dayjs() : undefined,
        }}
      >
        <Form.Item
          label="Stato Attuale"
        >
          <Input value={order.order_status?.status_name} disabled />
        </Form.Item>        <Form.Item
          label="Nuovo Stato"
          name="order_status_id"
          rules={[{ required: true, message: 'Seleziona un nuovo stato' }]}
        >
          <Select
            placeholder="Seleziona nuovo stato"
            onChange={(value) => {
              setSelectedStatusId(value);
              form.setFieldsValue({ order_status_id: value });
              // Se lo stato selezionato richiede data di consegna, inizializza con data odierna
              if (requiresDeliveryDate(value)) {
                form.setFieldsValue({ actual_delivery_date: dayjs() });
              }
            }}          >
            {allAvailableStatuses.map(status => (
              <Option key={status.id} value={status.id}>
                {status.status_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedStatusId && (
          <Alert
            message="Descrizione Stato"
            description={getStatusDescription(selectedStatusId)}
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        {selectedStatusId && requiresDeliveryDate(selectedStatusId) && (
          <Form.Item
            name="actual_delivery_date"
            label="Data Consegna Effettiva"
            rules={[{ required: true, message: 'Data consegna richiesta per questo stato' }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              placeholder="Seleziona data di consegna"
            />
          </Form.Item>
        )}

        {selectedStatusId && orderStatuses.find(s => s.id === selectedStatusId)?.status_name === 'In Produzione' && (
          <Alert
            message="Nessuna prenotazione automatica"
            description="Il passaggio a 'In Produzione' cambia solo lo stato dell'ordine: nessuna prenotazione viene creata. Le prenotazioni si gestiscono da Gestione Ordine."
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        {selectedStatusId && orderStatuses.find(s => s.id === selectedStatusId)?.status_name === 'Annullato' && (
          <Alert
            message="Attenzione"
            description="L'annullamento è definitivo e libera automaticamente tutte le prenotazioni dell'ordine: la merce torna disponibile per gli altri ordini. L'operazione viene registrata in Cronologia."
            type="warning"
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          label="Note"
          name="notes"
        >
          <TextArea
            rows={3}
            placeholder="Aggiungi note per il cambio stato..."
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={onCancel}>
              Annulla
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={loading}
              onClick={() => form.validateFields().then(handleSubmit)}
            >
              Aggiorna Stato
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OrderStatusModal;